import {
  Button,
  Modal,
  ModalBody,
  ModalContent,
  ModalDescription,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  ModalTitle,
} from '@apollo/orbit';
import { Switch } from '@apollo/space-kit/Switch';
import React, { useState } from 'react';
import { useLocation } from 'react-router';

import { useLocalStorage } from 'src/hooks/useLocalStorage';
import { useLDFlag } from 'src/lib/launchDarkly';

import {
  OperationState,
  useCopyLinkToOperation,
} from '../hooks/useCopyLinkToOperation';

/**
 * Modal to change explorer shareable operation link settings
 */
export const ShareableLinkSettingsModal = ({
  isOpen,
  onClose,
}: {
  isOpen: boolean;
  onClose: () => void;
}) => {
  // Grab the optional operation state
  const location = useLocation<{ operationState?: OperationState }>();
  const operationState =
    location.state &&
    typeof location.state === 'object' &&
    location.state.operationState;

  const [explorerLinkSettings, setExplorerLinkSettings] = useLocalStorage(
    'explorerLinkSettings',
  );
  const copyLinkToOperation = useCopyLinkToOperation();
  const [loading, setLoading] = useState(false);

  const shouldShowPostflightScripts = useLDFlag('nebula-postflight-scripts');

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="lg">
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>
          <ModalTitle>Shareable Operation Link Settings</ModalTitle>
          <ModalDescription>
            Update what to include in links generated for this document
          </ModalDescription>
        </ModalHeader>
        <ModalBody>
          <div>
            <Switch
              isSelected={explorerLinkSettings.includeVariables}
              onChange={(newIncludeVariables) => {
                setExplorerLinkSettings((currentSettings) => ({
                  ...currentSettings,
                  includeVariables: newIncludeVariables,
                }));
              }}
              className="w-full font-normal"
            >
              Include Variables
            </Switch>
            <Switch
              isSelected={explorerLinkSettings.includeHeaders}
              onChange={(newIncludeHeaders) => {
                setExplorerLinkSettings((currentSettings) => ({
                  ...currentSettings,
                  includeHeaders: newIncludeHeaders,
                }));
              }}
              className="w-full font-normal"
            >
              Include Headers
            </Switch>
            {shouldShowPostflightScripts ? (
              <>
                <Switch
                  isSelected={
                    explorerLinkSettings.includePreflightOperationScript
                  }
                  onChange={(newIncludePreflightOperationScript) => {
                    setExplorerLinkSettings((currentSettings) => ({
                      ...currentSettings,
                      includePreflightOperationScript:
                        newIncludePreflightOperationScript,
                    }));
                  }}
                  className="w-full font-normal"
                >
                  Include Pre-Operation Script
                </Switch>
                <Switch
                  isSelected={
                    explorerLinkSettings.includePostflightOperationScript
                  }
                  onChange={(newIncludePostflightOperationScript) => {
                    setExplorerLinkSettings((currentSettings) => ({
                      ...currentSettings,
                      includePostflightOperationScript:
                        newIncludePostflightOperationScript,
                    }));
                  }}
                  className="w-full font-normal"
                >
                  Include Post-Operation Script
                </Switch>
              </>
            ) : (
              <Switch
                isSelected={
                  explorerLinkSettings.includePreflightOperationScript
                }
                onChange={(newIncludePreflightOperationScript) => {
                  setExplorerLinkSettings((currentSettings) => ({
                    ...currentSettings,
                    includePreflightOperationScript:
                      newIncludePreflightOperationScript,
                  }));
                }}
                className="w-full font-normal"
              >
                Include Script
              </Switch>
            )}
          </div>
        </ModalBody>
        <ModalFooter>
          <label className="flex items-center">
            <input
              className="mr-1 flex size-3 flex-none cursor-pointer items-center rounded border p-0.5"
              type="checkbox"
              checked={explorerLinkSettings.preferencesSaved}
              onChange={() => {
                setExplorerLinkSettings((currentSettings) => ({
                  ...currentSettings,
                  preferencesSaved: !currentSettings.preferencesSaved,
                }));
              }}
            />
            Remember this preference
          </label>
          {operationState ? (
            <Button
              variant="primary"
              isLoading={loading}
              loadingText="Copy link"
              onClick={async () => {
                setLoading(true);
                await copyLinkToOperation(operationState);
                setLoading(false);
                onClose();
              }}
              type="button"
            >
              Copy Link
            </Button>
          ) : (
            <Button variant="primary" onClick={onClose} type="button">
              Done
            </Button>
          )}
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};
